import { Bell, Check, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNotifications, type AppNotification } from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';
import { useState } from 'react';

export function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleClick = async (notif: AppNotification) => {
    if (!notif.read_at) {
      await markAsRead(notif.id);
    }
    if (notif.href) {
      navigate(notif.href);
      setOpen(false);
    }
  };

  return (
    <>
      <Button variant="ghost" size="icon" className="relative h-9 w-9" onClick={() => setOpen(true)}>
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <Badge
            className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 text-[10px] font-bold flex items-center justify-center"
            variant="destructive"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Button>

      <ResponsiveOverlay
        open={open}
        onOpenChange={setOpen}
        variant="sidepanel"
        title="Notiser"
        showHeader
      >
        {/* Mark all read */}
        {unreadCount > 0 && (
          <div className="flex justify-end px-1 pb-2">
            <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={markAllAsRead}>
              <CheckCheck className="h-3.5 w-3.5" />
              Markera alla lästa
            </Button>
          </div>
        )}

        <ScrollArea className="max-h-[60vh] md:max-h-[calc(100vh-120px)]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Bell className="h-8 w-8 mb-3 opacity-30" />
              <p className="text-sm">Inga notiser</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notif) => (
                <button
                  key={notif.id}
                  onClick={() => handleClick(notif)}
                  className={cn(
                    'w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors flex gap-3',
                    !notif.read_at && 'bg-primary/5'
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {!notif.read_at && (
                        <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                      )}
                      <span className="font-medium text-sm truncate">{notif.title}</span>
                    </div>
                    {notif.body && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.body}</p>
                    )}
                    <p className="text-[11px] text-muted-foreground/60 mt-1">
                      {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true, locale: sv })}
                    </p>
                  </div>
                  {!notif.read_at && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 flex-shrink-0 self-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notif.id);
                      }}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </ResponsiveOverlay>
    </>
  );
}
